import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Save, X } from "lucide-react";

// Converte string pt-BR ("1,50") ou en-US ("1.50") para número; retorna NaN se vazio/inválido
const parseDec = (s) => {
  if (s === "" || s === null || s === undefined) return NaN;
  const normalized = String(s).replace(/\./g, "").replace(",", ".");
  const n = parseFloat(normalized);
  return isNaN(n) ? NaN : n;
};
// Formata número para exibir pt-BR com vírgula
const fmtDec = (n, min = 2, max = 2) => {
  if (n === null || n === undefined || n === "" || isNaN(n)) return "";
  return Number(n).toLocaleString("pt-BR", { minimumFractionDigits: min, maximumFractionDigits: max });
};

export default function Configuracoes() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef();
  const sigRef = useRef();
  const pixQrRef = useRef();

  useEffect(() => {
    api.get("/configuracoes").then(({ data }) => setCfg(data));
  }, []);

  if (!cfg) return <div className="text-zinc-500">Carregando...</div>;

  const set = (patch) => setCfg({ ...cfg, ...patch });

  const upload = async (file, field) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd);
    set({ [field]: data.url });
    toast.success("Imagem enviada");
  };

  const uploadVideo = async (file) => {
    if (file.size > 60 * 1024 * 1024) { toast.error("Arquivo acima de 60MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      set({ video_apresentacao: data.url });
      // Salvar imediatamente para não depender do botão
      await api.put("/configuracoes", { ...cfg, video_apresentacao: data.url });
      toast.success("Vídeo enviado e salvo");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar vídeo");
    }
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/configuracoes", cfg);
      toast.success("Configurações salvas");
    } catch (err) {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const pathFromUrl = (u) => (u || "").replace("/api/files/", "");

  return (
    <form onSubmit={salvar} className="space-y-6 max-w-3xl" data-testid="configuracoes-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
          BBH
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Configurações da Empresa</h1>
      </div>

      <section className="bbh-card p-5 space-y-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Identidade</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ImagePicker
            label="Logo"
            src={cfg.logo ? fileUrl(pathFromUrl(cfg.logo)) : null}
            onFile={(f) => upload(f, "logo")}
            onClear={() => set({ logo: "" })}
            inputRef={logoRef}
            testid="logo"
          />
          <ImagePicker
            label="Assinatura"
            src={cfg.assinatura ? fileUrl(pathFromUrl(cfg.assinatura)) : null}
            onFile={(f) => upload(f, "assinatura")}
            onClear={() => set({ assinatura: "" })}
            inputRef={sigRef}
            testid="assinatura"
          />
        </div>
      </section>

      <section className="bbh-card p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="col-span-full text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
          Dados da Empresa
        </div>
        <F label="Nome" testid="cfg-nome" v={cfg.nome_empresa} on={(v) => set({ nome_empresa: v })} />
        <F label="CNPJ" testid="cfg-cnpj" v={cfg.cnpj} on={(v) => set({ cnpj: v })} />
        <F label="Endereço" testid="cfg-endereco" v={cfg.endereco} on={(v) => set({ endereco: v })} span />
        <F label="WhatsApp" testid="cfg-whatsapp" v={cfg.whatsapp} on={(v) => set({ whatsapp: v })} />
        <F label="E-mail" testid="cfg-email" v={cfg.email} on={(v) => set({ email: v })} />
        <F label="Site" testid="cfg-site" v={cfg.site} on={(v) => set({ site: v })} />
      </section>

      <section className="bbh-card p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="col-span-full text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
          Responsável
        </div>
        <F label="Nome do responsável" testid="cfg-nome-assinante" v={cfg.nome_assinante}
          on={(v) => set({ nome_assinante: v })} />
        <F label="Cargo" testid="cfg-cargo" v={cfg.cargo} on={(v) => set({ cargo: v })} />
      </section>

      <section className="bbh-card p-5 space-y-4" data-testid="cfg-pagamentos">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Pagamentos</div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Tipo de chave PIX</label>
            <select
              value={cfg.pix_tipo_chave || ""}
              onChange={(e) => set({ pix_tipo_chave: e.target.value })}
              data-testid="cfg-pix-tipo"
              className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bg-white text-sm"
            >
              <option value="">Selecione...</option>
              <option value="cpf">CPF</option>
              <option value="cnpj">CNPJ</option>
              <option value="email">E-mail</option>
              <option value="telefone">Telefone</option>
              <option value="aleatoria">Chave aleatória</option>
            </select>
          </div>
          <F label="Chave PIX" testid="cfg-pix-chave" v={cfg.pix_chave} on={(v) => set({ pix_chave: v })} />
          <F label="Titular (opcional)" testid="cfg-pix-titular" v={cfg.pix_titular} on={(v) => set({ pix_titular: v })} span />
        </div>

        <div>
          <ImagePicker
            label="QR Code PIX (opcional)"
            src={cfg.pix_qr_code ? fileUrl(pathFromUrl(cfg.pix_qr_code)) : null}
            onFile={(f) => upload(f, "pix_qr_code")}
            onClear={() => set({ pix_qr_code: "" })}
            inputRef={pixQrRef}
            testid="pix-qr"
          />
          <button
            type="button"
            onClick={async () => {
              try {
                const { data } = await api.post("/configuracoes/gerar-pix-qr");
                set({ pix_qr_code: data.url });
                toast.success("QR Code PIX gerado automaticamente!");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Erro ao gerar QR Code");
              }
            }}
            data-testid="gerar-pix-qr-btn"
            className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
          >
            Gerar QR Code automático a partir da chave
          </button>
          <div className="mt-1 text-[11px] text-zinc-500">
            Preenche a chave, tipo e titular acima e clique para gerar automaticamente. Também pode enviar seu próprio QR via "Enviar".
          </div>
        </div>

        <div>
          <div className="mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold" data-testid="aviso-cartao-inativo">
            Pagamento por cartão está INATIVO no momento — clientes veem "Pagamento por cartão em implantação". As taxas abaixo ficam guardadas para quando reativar.
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">
            Taxas por parcelamento (% repassado ao cliente)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="flex items-center gap-2">
                <span className="text-sm w-10 text-zinc-500">{n}x</span>
                <DecimalInput
                  value={(cfg.installment_fees || {})[String(n)] ?? 0}
                  onChangeNumber={(v) => set({
                    installment_fees: { ...(cfg.installment_fees || {}), [String(n)]: v },
                  })}
                  min={0}
                  max={100}
                  testid={`cfg-fee-${n}x`}
                  suffix="%"
                  className="flex-1"
                />
              </div>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-zinc-500">
            Dica: inclua os 2% de margem BBH + a taxa % cobrada pela Stripe em cada opção.
          </div>
        </div>

        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">
            Taxa fixa por transação (R$)
          </div>
          <div className="flex items-center gap-2 max-w-xs">
            <span className="text-sm text-zinc-500">R$</span>
            <DecimalInput
              value={cfg.taxa_transacao ?? 0}
              onChangeNumber={(v) => set({ taxa_transacao: v })}
              min={0}
              max={999}
              testid="cfg-taxa-transacao"
              className="flex-1"
            />
          </div>
          <div className="mt-2 text-[11px] text-zinc-500">
            A Stripe cobra <strong>R$ 0,39 por transação</strong> além da taxa percentual. Como o cliente paga parcelado por item, essa taxa é somada em cada pagamento para que a BBH receba o valor integral da mão de obra.
          </div>
        </div>
      </section>

      <section className="bbh-card p-5 space-y-3" data-testid="cfg-landing">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
          Vídeo de apresentação (opcional)
        </div>
        {cfg.video_apresentacao ? (
          <div className="space-y-2">
            <video
              src={fileUrl(pathFromUrl(cfg.video_apresentacao))}
              controls
              playsInline
              className="w-full max-w-md rounded-md bg-black"
            />
            <button
              type="button"
              onClick={() => set({ video_apresentacao: "" })}
              data-testid="remover-video-apresentacao"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-zinc-200 text-xs font-medium hover:bg-zinc-50"
            >
              Remover vídeo
            </button>
          </div>
        ) : (
          <div>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bbh-btn-primary text-sm font-semibold cursor-pointer">
              <Upload className="w-4 h-4" />
              Enviar vídeo (até 60 MB)
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                hidden
                data-testid="upload-video-apresentacao"
                onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])}
              />
            </label>
            <div className="mt-2 text-[11px] text-zinc-500">
              Formatos suportados: MP4, MOV, WEBM. Vai aparecer em destaque no hero da sua landing page.
            </div>
          </div>
        )}
      </section>

      <div className="sticky bottom-0 bg-[#FAFAFA] py-3">
        <button
          type="submit"
          disabled={saving}
          data-testid="salvar-configuracoes-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md bbh-btn-primary text-sm font-semibold disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar alterações"}
        </button>
      </div>
    </form>
  );
}

function F({ label, v, on, testid, span }) {
  return (
    <div className={span ? "col-span-full" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</label>
      <input
        value={v || ""}
        onChange={(e) => on(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm bg-white"
      />
    </div>
  );
}

function ImagePicker({ label, src, onFile, onClear, inputRef, testid }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</label>
      <div className="mt-2 flex items-center gap-3">
        <div className="w-24 h-24 rounded-md border border-zinc-200 bg-white flex items-center justify-center overflow-hidden">
          {src ? <img src={src} alt="" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-zinc-400">Sem imagem</span>}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={`upload-${testid}-btn`}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-zinc-200 text-xs font-medium hover:bg-zinc-50"
          >
            <Upload className="w-3 h-3" /> Enviar
          </button>
          {src && (
            <button
              type="button"
              onClick={onClear}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs text-rose-600"
            >
              <X className="w-3 h-3" /> Remover
            </button>
          )}
          <input ref={inputRef} type="file" accept="image/*" hidden
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        </div>
      </div>
    </div>
  );
}

// Input decimal com teclado numérico e aceita vírgula ou ponto. Normaliza no onBlur.
function DecimalInput({ value, onChangeNumber, min = 0, max, placeholder, testid, className = "", suffix = "" }) {
  const [txt, setTxt] = useState(fmtDec(value, 2, 2));
  useEffect(() => { setTxt(fmtDec(value, 2, 2)); }, [value]);
  const handleChange = (e) => {
    // aceita somente dígitos, vírgula ou ponto
    const v = e.target.value.replace(/[^\d.,]/g, "");
    setTxt(v);
  };
  const handleBlur = () => {
    let n = parseDec(txt);
    if (isNaN(n)) n = 0;
    if (min !== undefined && n < min) n = min;
    if (max !== undefined && n > max) n = max;
    const rounded = Math.round(n * 100) / 100;
    setTxt(fmtDec(rounded, 2, 2));
    onChangeNumber(rounded);
  };
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <input
        type="text"
        inputMode="decimal"
        value={txt}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        data-testid={testid}
        className="flex-1 px-2 py-1.5 rounded-md border border-zinc-200 bg-white text-sm w-full"
      />
      {suffix && <span className="text-xs text-zinc-500">{suffix}</span>}
    </div>
  );
}
